/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, useState, ReactElement } from "react";

import { PrimaryButton } from "@fluentui/react/lib/Button";
import { Stack, StackItem } from "@fluentui/react/lib/Stack";
import { Text } from "@fluentui/react/lib/Text";
import { TextField } from "@fluentui/react/lib/TextField";

import {
  headerStackStyles,
  headerTextStyles,
  ContainerWrapper,
} from "Components/Common/SharedTableStyles.styles";
import { OperationStatus } from "Constants/OperationStatus";

import {
  errorMessageStyles,
  formStackStyles,
  textFieldStackStyles,
  textFieldStyles,
  textStyles,
} from "./AddComment.styles";
import { IAddCommentProps } from "./IAddCommentProps";

/**
 * The Add Comment component.
 * @param props {IAddCommentProps}
 */
export const AddComment = (props: IAddCommentProps): ReactElement => {
  const [textValue, setTextValue] = useState("");
  const { onAddCommentClicked, addCommentStatus } = props;

  useEffect(() => {
    if (addCommentStatus === OperationStatus.Succeeded) {
      setTextValue("");
    }
  }, [addCommentStatus]);

  return (
    <ContainerWrapper>
      <Stack styles={headerStackStyles}>
        <Text styles={headerTextStyles}>Comments</Text>
      </Stack>
      <Stack horizontal styles={formStackStyles}>
        <Stack tokens={{ padding: "0 0 0 1.5rem" }}>
          <StackItem>
            <Text styles={textStyles}>{"New Comment:"}</Text>
          </StackItem>
        </Stack>
        <Stack tokens={{ childrenGap: "2.25rem" }} styles={textFieldStackStyles}>
          <StackItem>
            <TextField
              styles={textFieldStyles}
              multiline
              placeholder={"Enter comment"}
              value={textValue}
              onChange={(_, newValue): void | boolean => setTextValue(newValue || "")}
            />
            {addCommentStatus === OperationStatus.Failed && (
              <Text styles={errorMessageStyles}>
                {"Could not add comment at this time, please try again"}
              </Text>
            )}
          </StackItem>
          <StackItem>
            <PrimaryButton
              disabled={!textValue || addCommentStatus === OperationStatus.Pending}
              text={addCommentStatus === OperationStatus.Pending ? "Please wait" : "Add Comment"}
              onClick={(): void => onAddCommentClicked(textValue)}
            />
          </StackItem>
        </Stack>
      </Stack>
    </ContainerWrapper>
  );
};
